/**
 * Slice 40 — Cleaner-assigned maintenance checks due on a given day.
 */
import { useMemo } from "react";
import { format } from "date-fns";
import {
  usePropertyPreventativeMaintenance,
  type PropertyPreventativeMaintenance,
} from "@/hooks/usePreventativeMaintenance";

export type DueMaintenanceCheck = {
  id: string;
  title: string;
  photoUrl: string | null;
  frequency: string;
  nextDue: string;
  hint: string | null;
};

function titleOf(p: PropertyPreventativeMaintenance): string {
  return (
    p.custom_title?.trim() ||
    p.preventative_maintenance_templates?.title?.trim() ||
    "Maintenance"
  );
}

function photoOf(p: PropertyPreventativeMaintenance): string | null {
  if (p.notes && /^https?:\/\//i.test(p.notes.trim())) return p.notes.trim();
  return null;
}

/** Due when next_scheduled_date is set and on/before the job day. */
export function isPpmDueOnDate(
  p: PropertyPreventativeMaintenance,
  dayIso: string,
): boolean {
  if (!p.enabled) return false;
  if (!p.next_scheduled_date) return false;
  return p.next_scheduled_date <= dayIso;
}

export function useDueCleanerMaintenanceChecks(
  propertyId: string | undefined,
  scheduledDate: string | null | undefined,
) {
  const { data: rows = [], isLoading } = usePropertyPreventativeMaintenance(propertyId);
  const day = scheduledDate || format(new Date(), "yyyy-MM-dd");

  const due = useMemo((): DueMaintenanceCheck[] => {
    return rows
      .filter(
        (p) =>
          p.default_assign_type === "cleaner" &&
          isPpmDueOnDate(p, day),
      )
      .map((p) => ({
        id: p.id,
        title: titleOf(p),
        photoUrl: photoOf(p),
        frequency: p.frequency,
        nextDue: p.next_scheduled_date || day,
        hint: null,
      }));
  }, [rows, day]);

  return { due, isLoading, day };
}

export function useDueTeamMaintenanceChecks(propertyId: string | undefined) {
  const { data: rows = [], isLoading } = usePropertyPreventativeMaintenance(propertyId);
  const today = format(new Date(), "yyyy-MM-dd");

  const due = useMemo(() => {
    return rows.filter(
      (p) =>
        (p.default_assign_type === "team" || p.default_assign_type === "owner") &&
        isPpmDueOnDate(p, today),
    );
  }, [rows, today]);

  return { due, isLoading, today };
}
